class BBCrewCommunityCarousel extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.getAttribute('data-section-id');
    this.autoplayEnabled = this.getAttribute('data-autoplay') === 'true';
    this.autoplayDelay = parseInt(this.getAttribute('data-autoplay-delay'), 10) || 5000;
    this.activeIndex = 0;
    this.autoplayTimer = null;
    this.lastTrigger = null;

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.showSlide = this.showSlide.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.startAutoplay = this.startAutoplay.bind(this);
    this.stopAutoplay = this.stopAutoplay.bind(this);
  }

  connectedCallback() {
    this.slider = this.querySelector('shoppable-videos');
    this.modal = this.querySelector('[data-bbcrew-modal]');
    this.modalSlides = Array.from(this.querySelectorAll('[data-bbcrew-modal-slide]'));
    this.openButtons = Array.from(this.querySelectorAll('[data-bbcrew-open]'));
    this.closeButtons = Array.from(this.querySelectorAll('[data-bbcrew-close]'));
    this.prevButton = this.querySelector('[data-bbcrew-modal-prev]');
    this.nextButton = this.querySelector('[data-bbcrew-modal-next]');
    this.muteButton = this.querySelector('[data-bbcrew-mute]');
    this.soundIcon = this.querySelector('[data-bbcrew-sound-icon]');
    this.muteIcon = this.querySelector('[data-bbcrew-mute-icon]');

    this.openButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.lastTrigger = button;
        this.openModal(parseInt(button.dataset.index, 10) || 0);
      });
    });

    this.closeButtons.forEach((button) => button.addEventListener('click', this.closeModal));
    if (this.prevButton) this.prevButton.addEventListener('click', () => this.showSlide(this.activeIndex - 1, true));
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.showSlide(this.activeIndex + 1, true));

    if (this.muteButton) {
      this.muteButton.addEventListener('click', () => {
        const video = this.getActiveVideo();
        if (!video) return;
        video.muted = !video.muted;
        this.updateMuteButton(video);
      });
    }

    this.querySelectorAll('[data-bbcrew-modal-play]').forEach((button) => {
      button.addEventListener('click', () => {
        const slide = button.closest('[data-bbcrew-modal-slide]');
        const video = slide?.querySelector('video');
        if (!video) return;

        if (video.paused) {
          this.playVideo(video);
        } else {
          video.pause();
          this.updatePlayButton(slide, false);
        }
      });
    });

    this.querySelectorAll('.bbcrew-community__modal-video').forEach((video) => {
      const slide = video.closest('[data-bbcrew-modal-slide]');
      video.addEventListener('play', () => this.updatePlayButton(slide, true));
      video.addEventListener('pause', () => this.updatePlayButton(slide, false));
      video.addEventListener('ended', () => this.updatePlayButton(slide, false));
    });

    this.addEventListener('mouseenter', this.stopAutoplay);
    this.addEventListener('mouseleave', this.startAutoplay);
    this.addEventListener('focusin', this.stopAutoplay);
    this.addEventListener('focusout', this.startAutoplay);
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.body.classList.remove('bbcrew-community-modal-open');
  }

  getActiveVideo() {
    return this.modalSlides[this.activeIndex]?.querySelector('video') || null;
  }

  activateSocialEmbed(slide) {
    const iframe = slide?.querySelector('[data-bbcrew-social-embed]');
    if (!iframe) return;

    const source = iframe.dataset.src;
    if (source && iframe.getAttribute('src') !== source) {
      iframe.setAttribute('src', source);
    }
  }

  deactivateSocialEmbed(slide) {
    const iframe = slide?.querySelector('[data-bbcrew-social-embed]');
    if (!iframe) return;

    // Unloading the iframe stops Instagram / YouTube playback when a modal
    // slide is hidden or the viewer is closed.
    iframe.setAttribute('src', 'about:blank');
  }

  updateMuteButton(video) {
    if (!this.muteButton) return;
    if (!video) {
      this.muteButton.hidden = true;
      return;
    }

    this.muteButton.hidden = false;
    this.soundIcon?.classList.toggle('hide', video.muted);
    this.muteIcon?.classList.toggle('hide', !video.muted);
    this.muteButton.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
  }

  updatePlayButton(slide, isPlaying) {
    if (!slide) return;
    const button = slide.querySelector('[data-bbcrew-modal-play]');
    if (!button) return;

    const playIcon = button.querySelector('[data-bbcrew-modal-play-icon]');
    const pauseIcon = button.querySelector('[data-bbcrew-modal-pause-icon]');
    button.classList.toggle('is-playing', isPlaying);
    playIcon?.classList.toggle('hide', isPlaying);
    pauseIcon?.classList.toggle('hide', !isPlaying);
    button.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
  }

  playVideo(video) {
    this.modalSlides.forEach((slide) => {
      const otherVideo = slide.querySelector('video');
      if (otherVideo && otherVideo !== video) {
        otherVideo.pause();
        this.updatePlayButton(slide, false);
      }
    });

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        video.muted = true;
        this.updateMuteButton(video);
        video.play().catch(() => {});
      });
    }
  }

  openModal(index) {
    if (!this.modal) return;
    this.stopAutoplay();
    this.showSlide(index, false);
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bbcrew-community-modal-open');

    const video = this.getActiveVideo();
    if (video) {
      video.currentTime = 0;
      video.muted = false;
      this.updateMuteButton(video);
      this.playVideo(video);
    } else {
      this.updateMuteButton(null);
    }

    requestAnimationFrame(() => {
      this.modal.querySelector('[data-bbcrew-close]:not(.bbcrew-community__modal-backdrop)')?.focus();
    });
  }

  closeModal() {
    if (!this.modal) return;

    this.modalSlides.forEach((slide) => {
      const video = slide.querySelector('video');
      if (video) video.pause();
      this.deactivateSocialEmbed(slide);
      this.updatePlayButton(slide, false);
    });

    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bbcrew-community-modal-open');
    this.updateMuteButton(null);

    if (this.lastTrigger) this.lastTrigger.focus();
    this.startAutoplay();
  }

  showSlide(index, autoplayVideo = true) {
    if (this.modalSlides.length === 0) return;

    const count = this.modalSlides.length;
    this.activeIndex = ((index % count) + count) % count;

    this.modalSlides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.activeIndex;
      slide.classList.toggle('hide', !isActive);

      const video = slide.querySelector('video');
      if (video && !isActive) {
        video.pause();
        video.currentTime = 0;
        this.updatePlayButton(slide, false);
      }

      if (isActive) {
        this.activateSocialEmbed(slide);
      } else {
        this.deactivateSocialEmbed(slide);
      }
    });

    const activeVideo = this.getActiveVideo();
    this.updateMuteButton(activeVideo);

    if (activeVideo && autoplayVideo && this.modal?.classList.contains('is-open')) {
      activeVideo.currentTime = 0;
      activeVideo.muted = false;
      this.updateMuteButton(activeVideo);
      this.playVideo(activeVideo);
    }
  }

  handleKeydown(event) {
    if (!this.modal?.classList.contains('is-open')) return;

    if (event.key === 'Escape') this.closeModal();
    if (event.key === 'ArrowLeft') this.showSlide(this.activeIndex - 1, true);
    if (event.key === 'ArrowRight') this.showSlide(this.activeIndex + 1, true);
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.stopAutoplay();
    } else if (!this.modal?.classList.contains('is-open')) {
      this.startAutoplay();
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayEnabled || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.autoplayTimer = window.setInterval(() => {
      if (this.modal?.classList.contains('is-open') || document.visibilityState !== 'visible') return;

      const swiper = this.slider?.swiper;
      if (!swiper || swiper.destroyed) return;

      if (swiper.isEnd) {
        swiper.slideTo(0);
      } else {
        swiper.slideNext();
      }
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

if (!customElements.get('bbcrew-community-carousel')) {
  customElements.define('bbcrew-community-carousel', BBCrewCommunityCarousel);
}
